import React, { useContext, useState } from "react";
import CustomTable from "../../components/CustomTable";
import ClassAdd from "./ClassAdd";
import useApi from "../../hooks/useApi";
import { ListClass, ListBranch, ListEmployee } from "../../constants/API/API";
import useFilter from "../../hooks/useFilter";
import ClassUpdate from "./ClassUpdate";
import usePermissions from "../../hooks/usePermissions";
import { permissionKeys } from "../../constants/staffRole";
import { AppStateContext } from "../../context/AppContext";
import { StatusChangeClass, deleteClass } from "../../constants/API/API";
import { message, Select, Space, Button, Popconfirm } from "antd";
import moment from "moment";
import { Switch } from "antd";
import { useEffect } from "react";
import { EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import ClassView from "./ClassView";
import DeleteRecord from "../../components/DeleteRecord";
import { useNavigate } from "react-router";

const Class = () => {
  const { isAdd, isDelete, isEdit, isView } = usePermissions({
    key: permissionKeys.Employees,
    redirectPermissions: ["isView"],
  });

  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Class Management");

  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(new Date().getTime());
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [data, setData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [branchList, setBranchList] = useState([]);
  const [filters, setFilters] = useFilter({
    sortField: "createdAt",
    sort: "desc",
    startDate: null,
    endDate: null,
    branchId: undefined,
    trainerId: undefined,
    roleType: undefined,
  });
  const [viewVisible, setViewVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchData() {
      try {
        const [branchRes, trainerRes] = await Promise.all([
          ListBranch(),
          ListEmployee({ role: "trainer" }),
        ]);
        setBranchList(branchRes.data.list || []);
      } catch (e) {
        message.error("Failed to fetch filter data");
        setBranchList([]);
      }
    }
    fetchData();
  }, []);

  const addClass = () => {
    setAddModal(true);
  };

  const handleStatusChange = async (checked, classes) => {
    setLoading(true);
    try {
      await StatusChangeClass(classes._id, { isActive: checked });
      message.success(
        `Class ${checked ? "activated" : "deactivated"} successfully`
      );
      setRefresh(new Date().getTime()); // Refresh list
    } catch (error) {
      message.error("Failed to update status");
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedClass(record);
    setEditModal(true);
  };

  const handleBranchFilter = (value) => {
    setFilters((prev) => ({
      ...prev,
      branchId: value,
      page: 1,
    }));
  };

  const handleTrainerFilter = (value) => {
    setFilters((prev) => ({
      ...prev,
      trainerId: value,
      page: 1,
    }));
  };

  const handleRoleTypeFilter = (value) => {
    setFilters((prev) => ({
      ...prev,
      roleType: value,
      page: 1,
    }));
  };

  const handleDelete = async (record) => {
    setLoading(true);
    try {
      await deleteClass(record._id, { isDelete: true });
      message.success("Class deleted successfully");
      setRefresh(new Date().getTime()); // Refresh list
    } catch (error) {
      message.error("Failed to delete class");
      setLoading(false);
    }
  };

  const handleView = (record) => {
    // setSelectedClass(record);
    // setViewVisible(true);
    navigate(`/classes/view/${record?._id}`);
  };

  useApi({
    callback: ListClass,
    payload: filters,
    dependencies: [refresh, filters],
    shouldCallApi: true,
    setLoading,
    onSuccess: ({ list, totalDocs }) => {
      setData(list);
      setTotalDocs(totalDocs);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_, item) => (
        <>
          <p>{item?.name}</p>
        </>
      ),
    },
    {
      title: "Branch",
      dataIndex: "branchDetails",
      key: "branchDetails",
      render: (_, item) => item?.branchId?.name || "-",
    },
    // {
    //   title: "Trainer",
    //   dataIndex: "trainerDetails",
    //   key: "trainerDetails",
    //   render: (_, item) =>
    //     Array.isArray(item?.trainers) && item.trainers.length > 0
    //       ? item.trainers.map((t) => t.name).join(", ")
    //       : "-",
    // },
    {
      title: "Days",
      dataIndex: "schedules",
      key: "days",
      render: (text, record) => {
        if (!record.schedules || !record.schedules.length) return "-";
        return record.schedules
          .map((schedule) => schedule.dayOfWeek)
          .join(", ");
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (text, record) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => handleStatusChange(checked, record)}
          disabled={!isEdit}
        />
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text, record) => moment(record.createdAt).format("ll"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          {isView && (
            <Button
              type="primary"
              onClick={() => handleView(record)}
              icon={<EyeOutlined />}
              title="View"
            />
          )}
          {isEdit && (
            <Button
              type="primary"
              onClick={() => handleEdit(record)}
              icon={<EditOutlined />}
              title="Edit"
            />
          )}
          {isDelete && (
            <DeleteRecord
              _id={record._id}
              callback={() => deleteClass(record._id, { isDeleted: true })}
              setLoading={setLoading}
              setRefresh={setRefresh}
              trigger={true}
              type="Class"
            />
          )}
        </Space>
      ),
    },
  ];

  const renderFilters = () => {
    return (
      <Space>
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Branch"
          onChange={handleBranchFilter}
          value={filters.branchId}
        >
          <Select.Option value="">All Branches</Select.Option>
          {branchList.map((branch) => (
            <Select.Option key={branch._id} value={branch._id}>
              {branch.name}
            </Select.Option>
          ))}
        </Select>
        {/* <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Trainer"
          onChange={handleTrainerFilter}
          value={filters.trainerId}
        >
          <Select.Option value="">All Trainers</Select.Option>
          {trainerList.map((trainer) => (
            <Select.Option key={trainer._id} value={trainer._id}>
              {trainer.name}
            </Select.Option>
          ))}
        </Select> */}
      </Space>
    );
  };

  return (
    <div>
      <CustomTable
        addFn={addClass}
        data={data}
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        totalDocs={totalDocs}
        searchField={["name", "trainerName"]}
        searchPlaceholder="Search by class Name or Trainer Name"
        customFilter={renderFilters}
      />
      {addModal && isAdd && (
        <ClassAdd
          visible={addModal}
          setVisible={setAddModal}
          refresh={setRefresh}
        />
      )}
      {editModal && isAdd && selectedClass && (
        <ClassUpdate
          editVisible={editModal}
          setEditVisible={setEditModal}
          selectedClass={selectedClass}
          refresh={setRefresh}
        />
      )}
      <ClassView
        viewVisible={viewVisible}
        setViewVisible={(visible) => {
          setViewVisible(visible);
          if (!visible) {
            setSelectedClass(null);
          }
        }}
        selectedClass={selectedClass}
      />
    </div>
  );
};

export default Class;
